import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { ShieldCheck, AlertCircle } from "lucide-react";

export default function ExecutorStep({
  executor,
  setExecutor,
  distribution,
}) {
  const set = (k) => (e) => setExecutor({ ...executor, [k]: e.target.value });

  const name = (executor.name || "").trim().toLowerCase();
  const backupName = (executor.backup_name || "").trim().toLowerCase();

  const beneficiaryNames = new Set();
  (distribution.percentages || []).forEach((p) => {
    if (p.beneficiary_name) beneficiaryNames.add(p.beneficiary_name.trim().toLowerCase());
  });
  (distribution.asset_assignments || []).forEach((a) =>
    (a.beneficiaries || []).forEach((b) =>
      beneficiaryNames.add((b || "").trim().toLowerCase())
    )
  );
  const executorIsBeneficiary = name && beneficiaryNames.has(name);
  const backupIsBeneficiary = backupName && beneficiaryNames.has(backupName);

  return (
    <div data-testid="step-executor">
      <h2 className="font-serif text-2xl text-navy">Executor</h2>
      <p className="mt-2 text-sm text-[#4A5568] sm:text-base">
        The executor is the person who will carry out your wishes. Trusted, responsible,
        and ideally younger than you. A backup is strongly recommended.
      </p>

      <Card className="mt-6 border-[#0A1128]/10">
        <CardContent className="p-6 sm:p-8">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-md bg-navy text-gold">
              <ShieldCheck size={18} />
            </div>
            <div className="font-serif text-lg text-navy">Primary Executor</div>
          </div>

          <div className="mt-5 grid gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <Label htmlFor="executor-name">Full name *</Label>
              <Input
                id="executor-name"
                className="mt-2"
                value={executor.name}
                onChange={set("name")}
                placeholder="e.g. Anil Sharma"
                data-testid="executor-name"
              />
            </div>
            <div>
              <Label htmlFor="executor-rel">Relationship *</Label>
              <Input
                id="executor-rel"
                className="mt-2"
                value={executor.relationship}
                onChange={set("relationship")}
                placeholder="Brother / Friend / Lawyer"
                data-testid="executor-rel"
              />
            </div>
            <div>
              <Label htmlFor="executor-phone">Phone number *</Label>
              <Input
                id="executor-phone"
                className="mt-2"
                value={executor.phone}
                onChange={set("phone")}
                placeholder="+91 9XXXXXXXXX"
                data-testid="executor-phone"
              />
            </div>
          </div>

          {executorIsBeneficiary && (
            <div
              className="mt-5 flex items-start gap-3 rounded-md border border-gold/50 bg-[#FBF7EE] p-4 text-sm text-navy"
              data-testid="executor-beneficiary-warning"
            >
              <AlertCircle size={18} className="mt-0.5 shrink-0 text-gold" />
              <div>
                <div className="font-medium">Heads up — your executor is also a beneficiary.</div>
                <p className="mt-1 text-[#4A5568]">
                  That's legally allowed in India, but can sometimes invite family disputes. Consider
                  appointing a neutral backup executor below.
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="mt-4 border-[#0A1128]/10">
        <CardContent className="p-6 sm:p-8">
          <div className="flex items-center justify-between">
            <div className="font-serif text-lg text-navy">Backup Executor</div>
            <span className="text-[10px] uppercase tracking-[0.18em] text-[#4A5568]">Recommended</span>
          </div>
          <p className="mt-1 text-sm text-[#4A5568]">
            In case the primary executor is unwilling or unable to act.
          </p>

          <div className="mt-1 text-xs text-[#4A5568] italic">Backup executor if primary is unavailable</div>
          <div className="mt-5 grid gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <Label htmlFor="exec-backup-name">Full name</Label>
              <Input
                id="exec-backup-name"
                className="mt-2"
                value={executor.backup_name || ""}
                onChange={set("backup_name")}
                placeholder="Optional"
                data-testid="executor-backup-name"
              />
            </div>
            <div>
              <Label htmlFor="exec-backup-rel">Relationship</Label>
              <Input
                id="exec-backup-rel"
                className="mt-2"
                value={executor.backup_relationship || ""}
                onChange={set("backup_relationship")}
                placeholder="Brother / Friend / Lawyer"
                data-testid="executor-backup-rel"
              />
            </div>
            <div>
              <Label htmlFor="exec-backup-phone">Phone number</Label>
              <Input
                id="exec-backup-phone"
                className="mt-2"
                value={executor.backup_phone || ""}
                onChange={set("backup_phone")}
                placeholder="+91 9XXXXXXXXX"
                data-testid="executor-backup-phone"
              />
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="exec-backup-email">Email</Label>
              <Input
                id="exec-backup-email"
                type="email"
                className="mt-2"
                value={executor.backup_email || ""}
                onChange={set("backup_email")}
                placeholder="backup@example.com"
                data-testid="executor-backup-email"
              />
            </div>
          </div>

          {backupIsBeneficiary && (
            <div
              className="mt-5 flex items-start gap-3 rounded-md border border-gold/50 bg-[#FBF7EE] p-4 text-sm text-navy"
              data-testid="executor-backup-warning"
            >
              <AlertCircle size={18} className="mt-0.5 shrink-0 text-gold" />
              <span>Backup executor is also listed as a beneficiary.</span>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
